import { Injectable } from '@angular/core';

import { Entity, Predicate, EntityQuery, FilterQueryOp } from 'breeze-client';
import { EntityManagerProvider } from './entity-manager-provider';
import { Entities, Guid, Job, JobStatus, JobType, TimeCategory } from './entities';
import { UnitOfWork } from './unit-of-work';
import { Repository } from './repository';
import * as moment from 'moment';

@Injectable({ providedIn: 'root' })
export class RAFUnitOfWork extends UnitOfWork {
    lookups: any;

    jobs: Repository<Entity>;
    jobMaterials: Repository<Entity>;
    jobTypes: Repository<Entity>;
    timeCategories: Repository<Entity>;
    timeEntries: Repository<Entity>;
    roles: Repository<Entity>;
    employees: Repository<Entity>;
    invoices: Repository<Entity>;
    invoiceLines: Repository<Entity>;
    emailMessages: Repository<Entity>;
    purchaseOrderLines: Repository<Entity>;
    purchaseOrderLineAttachments: Repository<Entity>;
    users: Repository<Entity>;
    userRoles: Repository<Entity>;

    constructor(emProvider: EntityManagerProvider) {
        super(emProvider);

        this.lookups = Entities.lookups;
        emProvider.prepare().then((lookups) => {
            this.lookups = lookups;
        });

        this.jobs = this.createRepository<Entity>('Job', 'jobs');
        this.jobMaterials = this.createRepository<Entity>('JobMaterial', 'jobMaterials');
        this.jobTypes = this.createRepository<Entity>('JobType', 'jobTypes');
        this.timeCategories = this.createRepository<Entity>('TimeCategory', 'timeCategories');
        this.timeEntries = this.createRepository<Entity>('TimeEntry', 'timeEntries');
        this.roles = this.createRepository<Entity>('Role', 'roles');
        this.employees = this.createRepository<Entity>('Employee', 'employees');
        this.invoices = this.createRepository<Entity>('Invoice', 'invoices');
        this.invoiceLines = this.createRepository<Entity>('InvoiceLine', 'invoiceLines');
        this.emailMessages = this.createRepository<Entity>('EmailMessage', 'emailMessages');
        this.purchaseOrderLines = this.createRepository<Entity>('PurchaseOrderLine', 'purchaseOrderLines');
        this.purchaseOrderLineAttachments = this.createRepository<Entity>('PurchaseOrderLineAttachment', 'purchaseOrderLineAttachments');
        this.users = this.createRepository<Entity>('User', 'users');
        this.userRoles = this.createRepository<Entity>('UserRole', 'userRoles');
    }

    async getUserInfos(options?) {
        let query = EntityQuery.from('UserInfos');
        if (options) {
            if (options.expand) {
                query = query.expand(options.expand);
            }
            if (options.orderby) {
                query = query.orderBy(options.orderby);
            }
            if (options.notId) {
                query = query.where('id', FilterQueryOp.NotEquals, options.notId);
            }
            if (options.userName) {
                query = query.where('userName', FilterQueryOp.Equals, options.userName);
            }
        }
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async changeUserPassword(user, password) {
        const query = EntityQuery.from('ChangeUserPassword')
            .withParameters({ id: user.id, password: password })
            .using(this.manager);

        const data = await this.manager.executeQuery(query);
        if (data.results && data.results.length > 0) return data.results[0];
        else return null;
    }

    async getNextJobNumberBasedOnMax() {
        const query = EntityQuery.from('Jobs').orderByDesc('jobNumber').take(1).select('jobNumber');
        const data = await this.manager.executeQuery(query);
        return data.results[0];
    }

    async getUserRoles(userId: number) {
        const query = EntityQuery.from('UserRoles').expand('role').where('userId', '==', userId);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getJobs() {
        const query = EntityQuery.from('Jobs').expand().orderByDesc('jobNumber').where('deleted', '==', null);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getActiveJobs() {
        const p1 = new Predicate('completed', '==',  null).and('deleted', '==', null).and('statusId', '==', JobStatus.Statuses.New);
        const p2 = new Predicate('completed', '==',  null).and('deleted', '==', null).and('statusId', '==', JobStatus.Statuses.Billing);
        const query = EntityQuery.from('jobs').expand('status').orderByDesc('jobNumber').where(p1.or(p2));
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getCustomerNames() {
        const query = EntityQuery.from('GetCustomerNames');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getCustomerBillingInformation(customerName: string) {
        const predicate = new Predicate('customerName', '==', customerName).and('deleted', '==', null).and('billingAddress1', '!=', null);
        const query = EntityQuery.from('Jobs').where(predicate).orderByDesc('id');
        const data = await this.manager.executeQuery(query);
        return data.results[0];
    }

    async getActiveEmployees(whereActive: boolean) {
        let predicate = new Predicate('deleted', '==', null).and('id', '!=', 1);
        if (!whereActive) predicate = predicate.and('endDate', '==', null);
        const query = EntityQuery.from('Employees').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getEmployeeByUserId(userId: number) {
        const predicate = new Predicate('deleted', '==', null).and('userId', '==', userId);
        const query = EntityQuery.from('Employees').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getEmployees() {
        const query = EntityQuery.from('Employees');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getTimeEntriesInfosByEmployeeId(employeeId: number, startDate: Date, endDate: Date) {
        const query = EntityQuery.from('GetEmployeeTimeInfos').withParameters({ employeeId, startDate, endDate });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getTimeEntriesByEmployeeId(employeeId: number, startDate: Date, endDate: Date) {
        const query = EntityQuery.from('GetEmployeeTime').withParameters({ employeeId, startDate, endDate });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getTimeEntriesByJobId(jobId: number, take?: number) {
        let predicate = new Predicate('deleted', '==', null).and('jobId', '==', jobId);
        let query = EntityQuery.from('TimeEntries').where(predicate);
        if (take) query = query.orderByDesc('id').take(take);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getTimeEntries(take: number) {
        let query = EntityQuery.from('TimeEntries').expand(['job']).where('deleted', '==', null);
        if (take) query = query.orderByDesc('id').take(take);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getTimeReview(startDate: Date, endDate: Date, unapprovedOnly: boolean) {
        const query = EntityQuery.from('TimeReview').withParameters({ startDate, endDate, unapprovedOnly });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getJobMaterialsByJobId(jobId: number, take?: number) {
        let predicate = new Predicate('deleted', '==', null).and('jobId', '==', jobId);
        let query = EntityQuery.from('JobMaterials').where(predicate);
        if (take) query = query.orderByDesc('id').take(take);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getInvoices(take?: number) {
        let query = EntityQuery.from('Invoices').expand(['job', 'invoiceLines']).orderByDesc('invoiceNumber').where('deleted', '==', null);
        if (take) query = query.orderByDesc('id').take(take);

        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPurchaseOrderLines() {
        const query = EntityQuery.from('PurchaseOrderLines').expand('job').orderByDesc('jobId').where('deleted', '==', null);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getPurchaseOrderLinesByJobId(jobId: number) {
        let predicate = new Predicate('deleted', '==', null);
        predicate = predicate.and('jobId', '==', jobId);
        const query = EntityQuery.from('PurchaseOrderLines').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPurchaseOrderLineAttachments(purchaseOrderLineId: number) {
        let predicate = new Predicate('deleted', '==', null).and('purchaseOrderLineId', '==', purchaseOrderLineId)
        const query = EntityQuery.from('PurchaseOrderLineAttachments').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getVendors() {
        const query = EntityQuery.from('PurchaseOrderLines').select('vendor').where('vendor', '!=', null);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getActiveTimeCategories() {
        const query = EntityQuery.from('TimeCategories').where('deleted', '==', null).orderBy('displayOrder, name');
        const data = await this.manager.executeQuery(query);
        return data.results as TimeCategory[];
    }

    async getActiveJobTypes() {
        const query = EntityQuery.from('JobTypes').where('deleted', '==', null).orderBy('displayOrder');
        const data = await this.manager.executeQuery(query);
        return data.results as JobType[];
    }
}
